import { useEffect, useState } from "react";
import api from "@/utils/axios-utils";
import { MathText } from "@ever-tutor/et-ui";

type Turn = { role: "student" | "agent"; content: string };
type WrongAnswer = {
  student_statement: string | null;
  reasoning: string | null;
  message_index: number | null;
};
type LoadState = "loading" | "ready" | "missing";

/**
 * The student/agent conversation of one practice attempt.
 *
 * Deliberately not /transcripts/{attempt_id}: that serves candidates.json,
 * which only the tutor pipeline writes, so for a practice bite it is always
 * empty.
 *
 * Agent turns are the raw transcript and carry inline <op> tags for whatever
 * the agent drew. Stripped for display — the drawing itself is right above in
 * the canvas snapshot.
 */
const OP_TAG = /<op>[\s\S]*?<\/op>|<\/?special_action[^>]*>/g;

const NotepadTranscriptView = ({
  sessionId,
  slideIdx,
  slideEndTs,
  studentName,
}: {
  sessionId: string;
  slideIdx: string | number;
  slideEndTs?: string;
  studentName: string;
}) => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [wrongAnswers, setWrongAnswers] = useState<WrongAnswer[]>([]);
  const [state, setState] = useState<LoadState>("loading");

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const load = async () => {
      try {
        const res = await api.get(
          `/analytics/sessions/${sessionId}/slides/${slideIdx}/notepad-transcript`,
          {
            params: slideEndTs ? { slide_end_ts: slideEndTs } : undefined,
            signal: controller.signal,
          },
        );
        if (cancelled) return;
        const loaded: Turn[] = Array.isArray(res.data?.turns)
          ? res.data.turns
          : [];
        setTurns(loaded);
        setState(loaded.length > 0 ? "ready" : "missing");
      } catch {
        if (!cancelled) setState("missing");
      }
    };

    // Best-effort and independent of the transcript: an attempt with no wrong
    // answers has no blob, and that must not blank out the conversation.
    const loadWrongAnswers = async () => {
      if (!slideEndTs) return;
      try {
        const res = await api.get(
          `/analytics/sessions/${sessionId}/slides/${slideIdx}/wrong-answers`,
          { params: { slide_end_ts: slideEndTs }, signal: controller.signal },
        );
        if (!cancelled && Array.isArray(res.data?.events)) {
          setWrongAnswers(res.data.events);
        }
      } catch {
        // Leaves the section hidden.
      }
    };

    load();
    loadWrongAnswers();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sessionId, slideIdx, slideEndTs]);

  if (state === "loading") {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e3eae1] border-t-[#138c61]" />
      </div>
    );
  }

  if (state === "missing") {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="rounded-[20px] border-2 border-[#EEEEEE] bg-[#FAFAFA] px-10 py-8 text-center">
          <p className="font-mulish font-semibold text-base text-[#25252599]">
            No conversation was recorded for this attempt.
          </p>
        </div>
      </div>
    );
  }

  // Listed separately rather than pinned onto the turns they refer to:
  // `message_index` counts the FLATTENED transcript the classifier saw, which
  // drops pairs that flatten to nothing, so it does not index these raw turns.
  const wrongAnswerSection = wrongAnswers.length > 0 && (
    <div className="mt-4 rounded-[20px] border-2 border-[#F3D9D9] bg-[#FFF9F9] px-[20px] py-[15px]">
      <p className="mb-[10px] text-sm font-medium italic text-gray-400">
        Why answers were marked incorrect
      </p>
      <div className="space-y-[12px]">
        {wrongAnswers.map((event, idx) => (
          <div key={idx} className="text-[15px] leading-snug">
            {event.student_statement && (
              <p className="font-mulish font-bold text-[#252525]">
                <MathText>{event.student_statement}</MathText>
              </p>
            )}
            {event.reasoning && (
              <p className="font-mulish text-[#25252599]">
                <MathText>{event.reasoning}</MathText>
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mt-4 flex flex-col gap-[10px] rounded-[30px] border-2 border-[#EEEEEE] bg-white px-[20px] py-[10px]">
      <div className="rounded-[20px] bg-[#FAFAFA] px-[20px] py-[30px] shadow-sm">
        <div className="space-y-[15px]">
          {turns.map((turn, idx) => {
            const text = turn.content.replace(OP_TAG, "").trim();
            if (!text) return null;
            return (
              <div key={idx} className="flex items-start gap-4">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border ${
                    turn.role === "student"
                      ? "border-blue-100 bg-[#e9f3ff]"
                      : "border-[#B3F285]/60 bg-[#B3F285]/40"
                  }`}
                >
                  <span className="text-sm font-bold text-[#1d372b]">
                    {turn.role === "student"
                      ? studentName.charAt(0).toUpperCase()
                      : "ET"}
                  </span>
                </div>
                <div className="flex-1 pt-2">
                  <p className="font-mulish text-[15px] leading-snug text-[#252525]">
                    <MathText>{text}</MathText>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {wrongAnswerSection}
    </div>
  );
};

export default NotepadTranscriptView;
